import React, { ElementType } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Button,
  useToast,
  StatLabel,
  Divider,
  Flex,
  Stat,
  StatGroup,
  StatHelpText,
  StatNumber,
  HStack,
  Image,
  VStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import {
  Order,
  useGetSettledOrdersByIdQuery,
  useGetRestaurantUnSettledOrdersQuery,
  useInsertSettlementMutation,
  useUpdateOrdersWithSettlementMutation,
} from '../utils/__generated__/graphql';
import { useGetImageUrl } from 'src/hooks';
import { FiArrowLeft, FiDollarSign } from 'react-icons/fi';
import { PageHeader, ModernCard, LoadingSpinner } from './shared';
import { useUserDefaultRole } from '@nhost/react';

// Utility functions
const calculateSum = (orders: Order[], selector: (order: Order) => number) =>
  orders.reduce((sum, order) => sum + selector(order), 0);

const getAdminCommission = (order: Order) =>
  ((order.basket.restaurant?.admin_commission ?? 0) / 100) * parseFloat(order.subTotal) +
  (order.deliveredBy === 'ADMIN' ? parseFloat(order.deliveryCharge) : 0);

const getRestaurantEarning = (order: Order) =>
  parseFloat(order.subTotal) +
  (order.deliveredBy === 'RESTAURANT' ? parseFloat(order.deliveryCharge) : 0) -
  ((order.basket.restaurant?.admin_commission ?? 0) / 100) * parseFloat(order.subTotal);

// Custom hook for settlement calculations
const useSettlementCalculations = (orders: Order[]) => {
  const totalOrders = orders.length;
  const dishAmount = calculateSum(orders, (order) => parseFloat(order.subTotal));
  const totalDeliveryCharge = calculateSum(orders, (order) => parseFloat(order.deliveryCharge));
  const totalAdminEarning = calculateSum(orders, getAdminCommission);
  const totalRestaurantEarning = calculateSum(orders, getRestaurantEarning);
  const totalAdminDeliveryAmount = calculateSum(orders, (order) =>
    order.deliveredBy === 'ADMIN' ? parseFloat(order.deliveryCharge) : 0
  );
  const totalRestaurantDeliveryAmount = calculateSum(orders, (order) =>
    order.deliveredBy === 'RESTAURANT' ? parseFloat(order.deliveryCharge) : 0
  );

  const totalRestaurantReceivable = calculateSum(orders, (order) =>
    order.deliveredBy === 'ADMIN' ? getRestaurantEarning(order) : 0
  );
  const totalReceivedByAdmin = calculateSum(orders, (order) =>
    order.deliveredBy === 'ADMIN' ? getAdminCommission(order) : 0
  );
  const totalAdminReceivable = calculateSum(orders, (order) =>
    order.deliveredBy === 'RESTAURANT' ? getAdminCommission(order) : 0
  );
  const totalReceivedByRestaurant = calculateSum(orders, (order) =>
    order.deliveredBy === 'RESTAURANT' ? getRestaurantEarning(order) : 0
  );

  const totalAmountOwed = totalRestaurantReceivable - totalAdminReceivable;
  const restaurant = orders.length ? orders[0].basket.restaurant : null;

  return {
    totalOrders,
    dishAmount,
    totalDeliveryCharge,
    totalAdminEarning,
    totalRestaurantEarning,
    totalRestaurantReceivable,
    totalReceivedByAdmin,
    totalAdminReceivable,
    totalReceivedByRestaurant,
    totalAmountOwed,
    totalAdminDeliveryAmount,
    totalRestaurantDeliveryAmount,
    restaurant,
  };
};

// Components
export const RestaurantSettlement: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const toast = useToast();
  const navigate = useNavigate();
  const isSettle = searchParams.get('type') === 'settled';

  const { data, loading, error } = isSettle
    ? useGetSettledOrdersByIdQuery({ variables: { settlementId: id }, fetchPolicy: 'network-only' })
    : useGetRestaurantUnSettledOrdersQuery({ variables: { restaurantId: id }, fetchPolicy: 'network-only' });

  const [createSettlement, { loading: createSettlementLoading }] = useInsertSettlementMutation();
  const [updateOrdersWithSettlement, { loading: updateOrdersLoading }] = useUpdateOrdersWithSettlementMutation();

  if (loading || createSettlementLoading || updateOrdersLoading)
    return <LoadingSpinner text="Loading settlement data..." />;
  if (error) {
    toast({
      title: 'Error',
      description: `Error fetching data: ${error.message}`,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Settlement Data
          </Text>
          <Text color="gray.600">Error fetching data: {error.message}</Text>
        </VStack>
      </ModernCard>
    );
  }

  const orders = (data?.orders ?? []) as Order[];
  const calculations = useSettlementCalculations(orders);

  const handleSettlement = async () => {
    try {
      const { data: settlementData } = await createSettlement({
        variables: {
          restaurantId: id,
          dishAmount: calculations.dishAmount,
          totalDeliveryCharge: calculations.totalDeliveryCharge,
          totalRestaurantEarning: calculations.totalRestaurantEarning,
          totalAdminEarning: calculations.totalAdminEarning,
        },
      });

      const settlementId = settlementData?.insert_settlement?.returning[0].id;

      if (settlementId) {
        await updateOrdersWithSettlement({
          variables: {
            settlementId,
            orders: orders?.map((order) => order.id),
          },
        });

        navigate(-1);
        toast({
          title: 'Settlement Successful',
          description: 'Orders have been settled successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error during settlement:', error);
      toast({
        title: 'Settlement Failed',
        description: 'An error occurred during settlement.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <PageHeader
        title={isSettle ? 'Settled Orders' : 'Unsettled Orders'}
        subtitle={`${orders.length} orders in this settlement`}
        actions={
          <Button
            leftIcon={<Icon as={FiArrowLeft as ElementType} />}
            colorScheme="gray"
            variant="outline"
            onClick={() => navigate(-1)}
            size={{ base: 'md', md: 'lg' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Back to Transactions</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Back</Text>
          </Button>
        }
      />

      <SalesReport
        {...calculations}
        isSettle={isSettle}
        handleSettlement={handleSettlement}
        createSettlementLoading={createSettlementLoading}
        updateOrdersLoading={updateOrdersLoading}
      />

      <ModernCard mt={{ base: 6, md: 8 }} overflow="hidden">
        <OrderTable orders={orders} />
      </ModernCard>
    </>
  );
};

// SalesReport component (props type defined inline for brevity)
const SalesReport: React.FC<
  ReturnType<typeof useSettlementCalculations> & {
    isSettle: boolean;
    handleSettlement: () => void;
    createSettlementLoading: boolean;
    updateOrdersLoading: boolean;
  }
> = (props) => {
  const { imageUrl: logoUrl } = useGetImageUrl(props.restaurant?.image);
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');
  const userRole = useUserDefaultRole();

  return (
    <ModernCard p={{ base: 6, md: 8 }}>
      <Heading as="h4" size="md" mb={{ base: 4, md: 6 }} color="gray.900" letterSpacing="tight">
        Sales Report
      </Heading>
      <StatGroup>
        <Stat>
          {props.restaurant && (
            <Flex>
              <Box px="2">
                <Image
                  src={logoUrl || '/assets/img/logo-placeholder.jpg'}
                  alt={props.restaurant.name}
                  boxSize="100px"
                  objectFit="cover"
                  borderRadius="md"
                />
              </Box>
              <Box>
                <Heading size="md">{props.restaurant.name}</Heading>
                <Text>Phone: {props.restaurant.phone}</Text>
                <Text>Email: {props.restaurant.email}</Text>
              </Box>
            </Flex>
          )}
        </Stat>

        <Stat>
          <StatLabel>Total Orders</StatLabel>
          <StatNumber>{props.totalOrders}</StatNumber>
        </Stat>

        <Stat>
          <StatLabel>Admin Earning</StatLabel>
          <StatNumber>Rs.{props.totalAdminEarning.toFixed(2)}</StatNumber>
          <StatHelpText>
            <Text>From Food: Rs.{(props.totalAdminEarning - props.totalAdminDeliveryAmount).toFixed(0)}</Text>
            <Text>From Delivery: Rs.{props.totalAdminDeliveryAmount.toFixed(0)}</Text>
          </StatHelpText>
        </Stat>

        <Stat>
          <StatLabel>Restaurant Earning</StatLabel>
          <StatNumber>Rs.{props.totalRestaurantEarning.toFixed(2)}</StatNumber>
          <StatHelpText>
            <Text>From Food: Rs.{(props.totalRestaurantEarning - props.totalRestaurantDeliveryAmount).toFixed(0)}</Text>
            <Text>From Delivery: Rs.{props.totalRestaurantDeliveryAmount.toFixed(0)}</Text>
          </StatHelpText>
        </Stat>
      </StatGroup>

      <Divider my={4} />

      <StatGroup>
        <Stat>
          <StatLabel>Total</StatLabel>
          <StatNumber>Rs.{(props.totalDeliveryCharge + props.dishAmount).toFixed(2)}</StatNumber>
        </Stat>

        <Stat>
          <StatLabel>Dish Amount</StatLabel>
          <StatNumber>Rs.{props.dishAmount.toFixed(2)}</StatNumber>
          <StatLabel>Total Delivery Charges</StatLabel>
          <StatNumber>Rs.{props.totalDeliveryCharge.toFixed(2)}</StatNumber>
        </Stat>

        <Stat>
          <StatLabel>Collected by Admin</StatLabel>
          <StatNumber>Rs.{(props.totalRestaurantReceivable + props.totalReceivedByAdmin).toFixed(2)}</StatNumber>
          <StatHelpText>
            <Text>Restaurant Receivable: Rs.{props.totalRestaurantReceivable.toFixed(2)}</Text>
            <Text>Received by Admin: Rs.{props.totalReceivedByAdmin.toFixed(2)}</Text>
          </StatHelpText>
        </Stat>

        <Stat>
          <StatLabel>Collected by Restaurant</StatLabel>
          <StatNumber>Rs.{(props.totalAdminReceivable + props.totalReceivedByRestaurant).toFixed(2)}</StatNumber>
          <StatHelpText>
            <Text>Admin Receivable: Rs.{props.totalAdminReceivable.toFixed(2)}</Text>
            <Text>Received by Restaurant: Rs.{props.totalReceivedByRestaurant.toFixed(2)}</Text>
          </StatHelpText>
        </Stat>
      </StatGroup>

      <Divider my={4} />

      <HStack justify="space-between" align="flex-end">
        <Stat>
          <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium" color={textColor}>
            Total Amount
          </Text>
          <StatLabel color={accentColor} fontSize={{ base: 'xs', md: 'sm' }}>
            {props.totalAmountOwed >= 0 ? 'ADMIN ===> RESTAURANT' : ' RESTAURANT ===> ADMIN'}
          </StatLabel>
          <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={accentColor}>
            Rs.{Math.abs(props.totalAmountOwed).toFixed(2)}
          </StatNumber>
        </Stat>
        {!props.isSettle && userRole === 'admin' && (
          <Button
            colorScheme="brand"
            size={{ base: 'md', md: 'lg' }}
            onClick={props.handleSettlement}
            isLoading={props.createSettlementLoading || props.updateOrdersLoading}
            leftIcon={<Icon as={FiDollarSign as ElementType} />}
            _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Settle Orders</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Settle</Text>
          </Button>
        )}
      </HStack>
    </ModernCard>
  );
};

// OrderTable component
const OrderTable: React.FC<{ orders: Order[] }> = ({ orders }) => (
  <Table variant="striped" colorScheme="teal">
    <Thead>
      <Tr>
        <Th>Order ID</Th>
        <Th>Order Date</Th>
        <Th>Total</Th>
        <Th>Dish Amount</Th>
        <Th>Delivery Charge</Th>
        <Th>Admin Commission</Th>
        <Th>Restaurant Earning</Th>
        {/* <Th>Delivered By</Th>
        <Th>Restaurant Receivable</Th>
        <Th>Received by Admin</Th>
        <Th>Admin Receivable</Th>
        <Th>Received by Restaurant</Th> */}
      </Tr>
    </Thead>
    <Tbody fontSize={{ base: 'md', md: 'lg' }}>
      {orders?.map((order) => {
        const adminCommission =
          ((order.basket.restaurant?.admin_commission ?? 0) / 100) * parseFloat(order.subTotal) +
          (order.deliveredBy === 'ADMIN' ? parseFloat(order.deliveryCharge) : 0);
        const restaurantEarning =
          parseFloat(order.subTotal) +
          (order.deliveredBy === 'RESTAURANT' ? parseFloat(order.deliveryCharge) : 0) -
          ((order.basket.restaurant?.admin_commission ?? 0) / 100) * parseFloat(order.subTotal);
        return (
          <Tr key={order.id}>
            <Td>
              <Link to={`/order/${order.id}`}>{order.id.slice(-5)}</Link>
            </Td>
            <Td>{new Date(order.created_at).toLocaleDateString()}</Td>
            <Td>{(parseFloat(order.subTotal) + parseFloat(order.deliveryCharge)).toFixed(2)}</Td>
            <Td>{parseFloat(order.subTotal).toFixed(2)}</Td>
            <Td>{parseFloat(order.deliveryCharge).toFixed(2)}</Td>
            <Td>{adminCommission.toFixed(2)}</Td>
            <Td>{restaurantEarning.toFixed(2)}</Td>
            {/* <Td>{order.deliveredBy}</Td>
            <Td>{order.deliveredBy === 'ADMIN' ? restaurantEarning.toFixed(2) : '0.00'}</Td>
            <Td>{order.deliveredBy === 'ADMIN' ? adminCommission.toFixed(2) : '0.00'}</Td>
            <Td>{order.deliveredBy === 'RESTAURANT' ? adminCommission.toFixed(2) : '0.00'}</Td>
            <Td>{order.deliveredBy === 'RESTAURANT' ? restaurantEarning.toFixed(2) : '0.00'}</Td> */}
          </Tr>
        );
      })}
    </Tbody>
  </Table>
);
